import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, TrendingUp, TrendingDown, Loader2, Sun, Moon, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Crowd level colors matching the main app
const crowdColors = {
  Low: { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' },
  Medium: { bg: 'bg-amber-50', text: 'text-amber-700', bar: 'bg-amber-500' },
  High: { bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' },
  Extreme: { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' }
};

/**
 * TimeSlider Component
 * Shows crowd level predictions across the entire day
 */
const TimeSlider = ({ airport, date, onTimeSelect, className = '' }) => {
  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [hoveredHour, setHoveredHour] = useState(null);

  // Get airport code - handles both string and object formats
  const airportCode = typeof airport === 'string' ? airport : airport?.code;

  // Fetch day predictions when airport or date changes
  useEffect(() => {
    if (!airportCode || !date) {
      setPredictions(null);
      return;
    }

    const fetchDayPredictions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(
          `${API_URL}/api/airport/day-predictions/${airportCode}?date=${date}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch predictions');
        }
        
        const data = await response.json();
        setPredictions(data);
        
        // Auto-select current hour or busiest hour
        const currentHour = new Date().getHours();
        if (currentHour >= 5 && currentHour < 24) {
          setSelectedHour(currentHour);
        } else {
          setSelectedHour(data.busiest_hour);
        }
      } catch (err) {
        setError(err.message);
        setPredictions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDayPredictions();
  }, [airportCode, date]);

  // Handle hour selection
  const handleHourClick = (hour) => {
    setSelectedHour(hour);
    if (onTimeSelect) {
      const timeStr = `${hour.toString().padStart(2, '0')}:00`;
      onTimeSelect(timeStr);
    }
  };

  // Get max score for scaling the bars
  const maxScore = predictions?.predictions
    ? Math.max(...predictions.predictions.map(p => p.crowd_score), 10)
    : 10;

  // Get currently viewed prediction (hovered or selected)
  const activePrediction = predictions?.predictions?.find(
    p => p.hour === (hoveredHour ?? selectedHour)
  );

  if (!airportCode || !date) {
    return null;
  }

  return (
    <Card className={`border-slate-200 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${className}`}>
      <CardContent className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#FF5E00]" />
            <h3 className="heading font-bold text-slate-900">Day Overview</h3>
          </div>
          {predictions && !predictions.is_live_data && (
            <span className="mono-label text-[9px] bg-amber-50 border border-amber-100 text-amber-700 px-2 py-1 rounded">
              Estimated
            </span>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            <span className="ml-2 text-slate-500">Loading day forecast...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center gap-2 text-red-500 py-4">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Predictions Chart */}
        {predictions && !loading && (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 mb-1">
                  <TrendingDown className="w-4 h-4" />
                  <span className="text-xs font-medium">Quietest</span>
                </div>
                <p className="text-sm font-semibold text-green-800">
                  {predictions.predictions.find(p => p.hour === predictions.quietest_hour)?.time}
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Busiest</span>
                </div>
                <p className="text-sm font-semibold text-red-800">
                  {predictions.predictions.find(p => p.hour === predictions.busiest_hour)?.time}
                </p>
              </div>
            </div>

            {/* Time Bar Chart */}
            <div className="relative">
              {/* Time labels */}
              <div className="flex justify-between text-xs text-slate-400 mb-2 px-1">
                <span className="flex items-center gap-1">
                  <Sun className="w-3 h-3" /> 5 AM
                </span>
                <span>12 PM</span>
                <span className="flex items-center gap-1">
                  <Moon className="w-3 h-3" /> 11 PM
                </span>
              </div>

              {/* Bars Container */}
              <div className="flex gap-0.5 sm:gap-1 h-24 items-end">
                {predictions.predictions.map((pred) => {
                  const heightPercent = Math.max((pred.crowd_score / maxScore) * 100, 8);
                  const colors = crowdColors[pred.crowd_level] || crowdColors.Low;
                  const isSelected = pred.hour === selectedHour;
                  const isHovered = pred.hour === hoveredHour;

                  return (
                    <motion.button
                      key={pred.hour}
                      initial={{ height: 0 }}
                      animate={{ height: `${heightPercent}%` }}
                      transition={{ duration: 0.3, delay: (pred.hour - 5) * 0.02 }}
                      onClick={() => handleHourClick(pred.hour)}
                      onMouseEnter={() => setHoveredHour(pred.hour)}
                      onMouseLeave={() => setHoveredHour(null)}
                      className={`
                        flex-1 rounded-none transition-opacity cursor-pointer relative
                        ${colors.bar}
                        ${isSelected ? 'ring-2 ring-offset-1 ring-slate-900' : ''}
                        ${isHovered ? 'opacity-80' : 'opacity-100'}
                      `}
                      style={{ minWidth: '8px' }}
                      title={`${pred.time}: ${pred.crowd_level}`}
                    >
                      {isSelected && (
                        <motion.div
                          layoutId="selected-indicator"
                          className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rounded-full"
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-3 mt-3 text-xs">
                {Object.entries(crowdColors).map(([level, colors]) => (
                  <div key={level} className="flex items-center gap-1">
                    <div className={`w-3 h-3 rounded-none ${colors.bar}`} />
                    <span className="text-slate-500">{level}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Time Details */}
            {activePrediction && (
              <motion.div
                key={activePrediction.hour}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-4 p-3 rounded-lg ${crowdColors[activePrediction.crowd_level]?.bg || 'bg-slate-100'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-semibold ${crowdColors[activePrediction.crowd_level]?.text || 'text-slate-700'}`}>
                      {activePrediction.time}
                    </p>
                    <p className="text-sm text-slate-600">
                      {activePrediction.crowd_level} Traffic
                    </p>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <p>{activePrediction.departures_count} departures</p>
                    <p>{activePrediction.arrivals_count} arrivals</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Action hint */}
            <p className="text-xs text-slate-400 text-center mt-3">
              Click a bar to see details and update your search time
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeSlider;
